// The messages between the translation review app (/admin/translate/) and a
// page of the site it shows in a frame (snapshot.ts and bridge.ts here), sent
// with postMessage, to and from the same origin only. See "Translation review"
// in CLAUDE.md.
import type { Anchor as ScrollAnchor } from "./sync.ts";

export type { ScrollAnchor };

// On a page's address, it asks the page to work with the app. Only a frame of
// the app, on the same origin, takes any notice.
export const REVIEW_PARAM = "translation-review";
export const REVIEW_APP = "/admin/translate/";

// "mark": a click on the page's text is a note about it. "use": the page works
// as it does for a visitor, but for leaving it.
export type Mode = "mark" | "use";

export interface Rect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// A popup of the page (a Materialize modal in its html), by its node's index in
// the page's list of nodes.
export interface Popup {
  at: number;
  label: string;
}

// From a page to the app. Node indexes (`at`) are in the page's own list.
export type FromFrame =
  // The page's nodes, as listed before its scripts ran (snapshot.ts).
  | { type: "ready"; tokens: string[]; popups: Popup[] }
  | { type: "hover"; id: string | null }
  // A click on a segment, or text selected in one. The rectangle is in the
  // page's viewport.
  | { type: "pick"; id: string; selection: { text: string; hint: number } | null; rect: Rect }
  | { type: "notice"; message: string }
  // A plain click on a link, which notes its text.
  | { type: "link" }
  // A link to follow: the app opens its page, as both pages.
  | { type: "navigate"; href: string }
  | { type: "scroll"; anchor: ScrollAnchor }
  // A click in "use" mode, to make on the other page too.
  | { type: "click"; at: number }
  // The popup now open, if any.
  | { type: "popup"; at: number | null };

// From the app to a page.
export type ToFrame =
  // The segments, as [node index, segment id].
  | { type: "tag"; marks: [number, string][] }
  | { type: "mode"; mode: Mode }
  | { type: "hover"; id: string | null }
  | { type: "marks"; noted: string[]; selected: string | null }
  | { type: "scroll"; anchor: ScrollAnchor }
  | { type: "click"; at: number }
  | { type: "popup"; at: number | null };

// Where a page is shown in a frame: its address with the parameter, whose
// value changes when the app loads the page afresh (Refresh), so the frame
// doesn't show what the browser kept.
export function frameAddress(path: string, load = 1): string {
  const url = new URL(path, "https://site.invalid");
  url.searchParams.set(REVIEW_PARAM, String(load));
  return url.pathname + url.search;
}
